import React from 'react';
import { gql, useQuery } from '@apollo/client';
import Card from '../../../../ui/canon/Card';
import Button from '../../../../ui/canon/Button';
import { formatCurrency } from '../../utils/formatters';

const GET_CU_GOAL = gql`
  query GetCuGoalForDcf {
    getCurrentUser {
      id
      valuation_last_accessed_date
      readiness_current_investable_assets
      triggering_events(limit: 1, event_type: "wealth_gap") {
        event_type
        payload
      }
    }
  }
`;

const GET_VAL = gql`
  query GetValForDcf($input: Input) {
    successionValuationData(input: $input) {
      valueOfPractice
    }
  }
`;

const currency = (n) => formatCurrency(Number(n || 0));

// Projection helpers and status icons
const DEFAULT_GROWTH = 0.06;

const computeYearsToGoal = (current, goal, growth = DEFAULT_GROWTH, maxYears = 10) => {
  const numericCurrent = Number(current || 0);
  const numericGoal = Number(goal || 0);
  if (numericGoal <= 0) return 0;
  if (numericCurrent >= numericGoal) return 0;
  let year = 0;
  let value = numericCurrent;
  while (year < maxYears && value < numericGoal) {
    year += 1;
    value = value * (1 + growth);
    if (value >= numericGoal) return year;
  }
  return null; // not reached within maxYears
};

const IconWarning = ({ className = '', style = {}, size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" className={className} style={style} aria-hidden="true">
    <path d="M9.049 2.927a1 1 0 011.902 0l7.003 14.006A1 1 0 0117.003 18H2.997a1 1 0 01-.951-1.067L9.049 2.927z" stroke="currentColor" fill="none"/>
    <path d="M10 7v5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    <circle cx="10" cy="14.5" r="1" fill="currentColor"/>
  </svg>
);

const IconSuccess = ({ className = '', style = {}, size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" className={className} style={style} aria-hidden="true">
    <circle cx="10" cy="10" r="8" stroke="currentColor" fill="none"/>
    <path d="M6.5 10.5l2.5 2.5 4.5-5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const IconTrophy = ({ className = '', style = {}, size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className={className} style={style} aria-hidden="true">
    <path d="M8 4h8v2a4 4 0 01-8 0V4z" stroke="currentColor" strokeWidth="1.5"/>
    <path d="M12 10v3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    <path d="M9 20h6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    <path d="M7 6H5a2 2 0 000 4h2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    <path d="M17 6h2a2 2 0 110 4h-2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    <path d="M9 17h6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
  </svg>
);

const EmptyState = ({ hasWealthGapEvent, hasValuation }) => (
  <div className="px-4 py-5 sm:p-6 h-full flex items-center justify-center text-center">
    <div>
      <h3 className="mb-2">Value Gap</h3>
      <p className="text-succession-text-secondary text-sm mb-3">Do you have enough business value to reach your exit? Find out if your business is on track for your exit.</p>
      {!hasWealthGapEvent ? (
        <Button type="button" onClick={() => { window.location.href = '/succession/plans'; }}>
          Start Succession Plan
        </Button>
      ) : (
        <Button as="a" href="/succession/valuation">Get Valuation</Button>
      )}
    </div>
  </div>
);

const Projection = ({ current, goal, growth = DEFAULT_GROWTH }) => {
  const bars = [];
  let year = 0;
  let v = Number(current || 0);
  const maxYears = 10;
  while (year < maxYears && v < goal) {
    year += 1;
    v = v * (1 + growth);
    bars.push({ year, value: v });
  }
  const yearsToGoal = computeYearsToGoal(current, goal, growth, maxYears);
  const maxVal = Math.max(goal, ...bars.map((b) => b.value), current);
  const scale = (x) => (maxVal > 0 ? (x / maxVal) : 0);

  return (
    <div className="mt-3 flex flex-col items-center justify-center text-center">
      <div className="relative" style={{ width: 260, height: 160 }}>
        <svg viewBox="0 0 260 160">
          {/* Goal line */}
          <line x1="10" x2="250" y1={160 - 130 * scale(goal)} y2={160 - 130 * scale(goal)} stroke="var(--succession-success, #10b981)" strokeDasharray="4 4" />
          {/* Goal label */}
          <text
            x={248}
            y={(160 - 130 * scale(goal)) - 6}
            textAnchor="end"
            fontSize="10"
            fill="var(--succession-text-secondary, #94a3b8)"
          >
            {`Goal ${currency(goal)}`}
          </text>
          {/* Bars */}
          {bars.map((b, i) => {
            const barW = 14;
            const gap = 6;
            const x = 20 + i * (barW + gap);
            const h = 130 * scale(b.value);
            const y = 160 - h;
            return <rect key={b.year} x={x} y={y} width={barW} height={h} fill="var(--succession-primary, #4f46e5)" opacity="0.6" rx="2" />;
          })}
        </svg>
      </div>
      {yearsToGoal != null ? (
        <div className="text-xs text-succession-text-secondary mt-1">~{yearsToGoal} {yearsToGoal === 1 ? 'year' : 'years'} to reach goal at {(growth * 100).toFixed(0)}% growth</div>
      ) : (
        <div className="text-xs text-succession-text-secondary mt-1">Projection (max 10y) at {(growth * 100).toFixed(0)}% growth</div>
      )}
    </div>
  );
};

const Snapshot = ({ currentValue, wealthGoal, assets = 0 }) => {
  const gap = Number(wealthGoal || 0) - Number(assets || 0) - Number(currentValue || 0);
  const isGapPositive = gap > 0;
  const pulseKeyframes = `@keyframes valuePulse { 0%,100% { transform: scale(1); opacity: 1; } 50% { transform: scale(1.04); opacity: 0.9; } }`;
  return (
    <div className="px-4 py-5 sm:p-6 h-full flex flex-col">
      <h3 className="mb-1">Value Gap</h3>
      <h4 className="text-sm text-succession-text-secondary">The shortfall between today’s value and your exit goal.</h4>
      <div className="grid grid-cols-2 gap-6 mt-3 text-center">
        <div>
          {isGapPositive && <style>{pulseKeyframes}</style>}
          <h3
            className={`mb-1 font-semibold ${isGapPositive ? 'text-succession-error' : 'text-succession-success'}`}
            style={isGapPositive ? { animation: 'valuePulse 1.6s ease-in-out infinite' } : undefined}
          >
            {currency(gap)}
          </h3>
          <div className="text-xs text-succession-text-secondary">Your Value Gap</div>
        </div>
        <div>
          <h3 className="mb-1 text-succession-text-primary font-semibold">{currency(currentValue)}</h3>
          <div className="text-xs text-succession-text-secondary">Current Value (DCF)</div>
        </div>
      </div>
      <div className="mt-2">
        <Projection current={currentValue} goal={wealthGoal} />
      </div>
      {/* Status message */}
      {(() => {
        const yearsToGoal = computeYearsToGoal(currentValue, wealthGoal, DEFAULT_GROWTH);
        const alreadyAchieved = Number(currentValue || 0) >= Number(wealthGoal || 0);
        if (alreadyAchieved) {
          return (
            <div className="mt-3 flex items-center justify-center gap-2 text-sm">
              <IconTrophy style={{ color: 'var(--succession-success, #10b981)' }} />
              <span className="text-succession-text-secondary">Congratulations on closing the value gap!</span>
            </div>
          );
        }
        if (yearsToGoal != null) {
          return (
            <div className="mt-3 flex items-center justify-center gap-2 text-sm">
              <IconSuccess style={{ color: 'var(--succession-success, #10b981)' }} />
              <span className="text-succession-text-secondary">Will close the value gap in {yearsToGoal} {yearsToGoal === 1 ? 'year' : 'years'}</span>
            </div>
          );
        }
        return (
          <div className="mt-3 flex items-center justify-center gap-2 text-sm">
            <IconWarning style={{ color: 'var(--succession-warning, #f59e0b)' }} />
            <span className="text-succession-text-secondary">Gap unlikely to close within 10 years</span>
          </div>
        );
      })()}
      <div className="mt-auto pt-2 flex justify-center">
        <a href="/succession/value-acceleration" className="text-sm text-succession-primary hover:text-succession-primary-hover">Review Value Metrics &gt;</a>
      </div>
    </div>
  );
};

const ValueGapDcfSnapshot = () => {
  const { data: cu, loading: cuLoading, error: cuError } = useQuery(GET_CU_GOAL, { fetchPolicy: 'cache-first', nextFetchPolicy: 'cache-first' });
  const lastAccess = cu?.getCurrentUser?.valuation_last_accessed_date;
  const wgPayload = cu?.getCurrentUser?.triggering_events?.[0]?.payload || {};
  const wealthGoal = Number(wgPayload.goal_lump_sum || 0);
  const assets = Number(cu?.getCurrentUser?.readiness_current_investable_assets || 0);
  const hasWealthGapEvent = wealthGoal > 0; // require a valid wealth gap event to show this card

  // Always declare hooks consistently; skip valuation fetch when not applicable
  const skipVal = cuLoading || !lastAccess || !hasWealthGapEvent;
  const { data: valData, loading, error } = useQuery(GET_VAL, { variables: { input: null }, skip: skipVal, fetchPolicy: 'network-only', nextFetchPolicy: 'cache-first' });

  if (cuLoading || loading) return <div className="p-4 text-succession-text-secondary text-sm">Loading…</div>;
  if (cuError || error) return <div className="p-4 text-succession-error text-sm">Unable to load.</div>;

  const currentValue = (!skipVal && valData) ? Number(valData?.successionValuationData?.valueOfPractice || 0) : null;
  const hasData = (currentValue != null && lastAccess && hasWealthGapEvent);

  return (
    <Card className="h-full overflow-hidden" data-value-gap-card="true">
      {hasData ? (
        <Snapshot currentValue={currentValue} wealthGoal={wealthGoal} assets={assets} />
      ) : (
        <EmptyState hasWealthGapEvent={hasWealthGapEvent} hasValuation={!!lastAccess} />
      )}
    </Card>
  );
};

export default ValueGapDcfSnapshot;


